import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HowItWorksSection = () => {
  const [steps, setSteps] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [progressHeight, setProgressHeight] = useState(0);
  const [visibleSteps, setVisibleSteps] = useState(new Set());
  
  const sectionRef = useRef(null);
  const stepsContainerRef = useRef(null);
  const stepRefs = useRef([]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSteps(response.data.how_it_works || []);
      } catch (error) {
        console.error('Failed to fetch settings:', error);
        // Fallback to default steps
        setSteps([
          { id: '1', step: '01', title: 'Create Your Will', description: 'Answer simple questions and our AI-powered system generates a legally compliant will in under 5 minutes. No legal jargon, just clarity.', image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=300&fit=crop' },
          { id: '2', step: '02', title: 'Secure Your Assets', description: 'Upload and organize all your important documents in our encrypted LifeVault. From property papers to insurance policies, everything stays safe.', image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=400&h=300&fit=crop' },
          { id: '3', step: '03', title: 'Protect Your Loved Ones', description: 'Designate trusted nominees who will be guided through every step when the time comes. They will never feel lost or alone.', image: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=400&h=300&fit=crop' },
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // IntersectionObserver for step reveal animations - higher threshold for later reveal
  useEffect(() => {
    if (steps.length === 0) return;

    // Looser thresholds on mobile so cards reveal as soon as their top edge
    // enters the viewport (avoids the perceived "black space" lag on phones).
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 1024px)').matches;
    const observerOptions = isMobile
      ? {
          root: null,
          rootMargin: '0px 0px -10% 0px',
          threshold: 0.15,
        }
      : {
          root: null,
          rootMargin: '-20% 0px -20% 0px', // Card must be 20% into viewport from top/bottom
          threshold: 0.4, // 40% of the card must be visible
        };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = parseInt(entry.target.dataset.stepIndex, 10);
          setVisibleSteps(prev => {
            const newSet = new Set(prev);
            newSet.add(index);
            return newSet;
          });
        }
      });
    }, observerOptions);

    // Small delay to ensure refs are populated
    const timeoutId = setTimeout(() => {
      stepRefs.current.forEach((ref) => {
        if (ref) observer.observe(ref);
      });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      stepRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, [steps]);

  // Scroll handler for active step and progress line
  const handleScroll = useCallback(() => {
    if (!stepsContainerRef.current || steps.length === 0) return;

    const containerRect = stepsContainerRef.current.getBoundingClientRect();
    const viewportMiddle = window.innerHeight / 2;

    // Calculate progress based on scroll position within the steps container
    const containerTop = containerRect.top;
    const containerHeight = containerRect.height;
    const scrollProgress = Math.max(0, Math.min(1, 
      (viewportMiddle - containerTop) / containerHeight
    ));
    setProgressHeight(scrollProgress * 100);

    // Find the step closest to viewport middle
    let closestIndex = -1;
    let closestDistance = Infinity;

    stepRefs.current.forEach((ref, index) => {
      if (!ref) return;
      const rect = ref.getBoundingClientRect();
      const stepMiddle = rect.top + rect.height / 2;
      const distance = Math.abs(stepMiddle - viewportMiddle);

      if (distance < closestDistance && rect.top < window.innerHeight && rect.bottom > 0) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    setActiveIndex(closestIndex);
  }, [steps.length]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial call
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  if (isLoading) {
    return (
      <section id="how-it-works" className="py-16 lg:py-24 bg-slate-950">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400"></div>
        </div>
      </section>
    );
  }

  if (steps.length === 0) {
    return null;
  }

  return (
    <section 
      id="how-it-works" 
      ref={sectionRef}
      className="py-16 lg:py-24 bg-slate-950"
    >
      <style>{`
        /* Step card base state - MORE DRAMATIC hidden state */
        .step-card {
          opacity: 0;
          transform: translateY(60px) scale(0.95);
          filter: blur(12px);
          transition: 
            opacity 700ms cubic-bezier(0.4, 0, 0.2, 1),
            transform 700ms cubic-bezier(0.4, 0, 0.2, 1),
            filter 700ms cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        /* Step card revealed state */
        .step-card.is-visible {
          opacity: 1;
          transform: translateY(0px) scale(1);
          filter: blur(0px);
        }
        
        /* Active step highlight - MORE PRONOUNCED elevation and glow */
        .step-card.is-visible.is-active {
          transform: translateY(-8px) scale(1.02);
        }
        
        .step-card .step-content-wrapper {
          border: 1px solid rgba(71, 85, 105, 0.6);
          border-radius: 1.5rem;
          padding: 1.5rem;
          background: rgba(30, 41, 59, 0.8);
          box-shadow: 
            0 4px 20px rgba(0, 0, 0, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.05);
          transition: 
            border-color 400ms ease,
            box-shadow 400ms ease,
            background 400ms ease;
        }
        
        .step-card.is-active .step-content-wrapper {
          border-color: rgba(245, 158, 11, 0.7);
          box-shadow: 
            0 0 0 1px rgba(245, 158, 11, 0.3),
            0 12px 40px rgba(245, 158, 11, 0.2),
            0 4px 20px rgba(0, 0, 0, 0.4),
            inset 0 1px 0 rgba(255, 255, 255, 0.08);
          background: rgba(30, 41, 59, 0.95);
        }
        
        /* Step number styling */
        .step-number {
          color: rgba(251, 191, 36, 0.35);
          filter: drop-shadow(0 0 8px rgba(251, 191, 36, 0.15));
          transition: color 400ms ease, filter 400ms ease;
        }
        
        .step-card.is-active .step-number {
          color: rgba(245, 158, 11, 0.7);
          filter: drop-shadow(0 0 16px rgba(245, 158, 11, 0.4));
        }
        
        /* Non-active cards are slightly dimmed */
        .step-card.is-visible:not(.is-active) {
          opacity: 0.6;
        }
        
        .step-card.is-visible:not(.is-active):hover {
          opacity: 0.85;
        }
        
        /* Progress rail - THICKER and more visible */
        .progress-rail {
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 3px;
          background: rgba(100, 116, 139, 0.25);
          border-radius: 2px;
        }
        
        /* Progress fill - GLOWING effect */
        .progress-fill {
          position: absolute;
          left: 0;
          top: 0;
          width: 3px;
          background: linear-gradient(to bottom, #fbbf24, #f59e0b, #d97706);
          border-radius: 2px;
          transition: height 150ms linear;
          box-shadow: 0 0 12px rgba(245, 158, 11, 0.5);
        }
        
        /* Step dot on progress line - LARGER */
        .step-dot {
          position: absolute;
          left: -5px;
          width: 13px;
          height: 13px;
          border-radius: 50%;
          background: rgba(51, 65, 85, 0.8);
          border: 2px solid rgba(15, 23, 42, 1);
          transition: 
            background 300ms ease,
            transform 300ms ease,
            box-shadow 300ms ease;
          z-index: 2;
        }
        
        .step-dot.is-passed {
          background: #f59e0b;
        }
        
        .step-dot.is-active {
          background: #fbbf24;
          transform: translateY(-50%) scale(1.4) !important;
          box-shadow: 0 0 16px rgba(245, 158, 11, 0.6);
        }
        
        /* Staggered animation delays for each card */
        .step-card[data-step-index="0"] {
          transition-delay: 0ms;
        }
        .step-card[data-step-index="1"] {
          transition-delay: 150ms;
        }
        .step-card[data-step-index="2"] {
          transition-delay: 300ms;
        }
        
        /* Responsive adjustments */
        @media (max-width: 1024px) {
          .progress-container {
            display: none;
          }
          
          .steps-wrapper {
            padding-left: 0 !important;
          }
          
          /* On mobile, don't dim non-active cards as much */
          .step-card.is-visible:not(.is-active) {
            opacity: 0.85;
          }

          /* Lighter, GPU-friendly entrance on mobile: drop blur, smaller translate, faster timing */
          .step-card {
            transform: translateY(24px);
            filter: none;
            transition:
              opacity 400ms cubic-bezier(0.4, 0, 0.2, 1),
              transform 400ms cubic-bezier(0.4, 0, 0.2, 1);
          }
          .step-card.is-visible {
            transform: translateY(0);
          }
          /* Disable per-card stagger delay on mobile so the first card doesn't wait */
          .step-card[data-step-index="0"],
          .step-card[data-step-index="1"],
          .step-card[data-step-index="2"] {
            transition-delay: 0ms;
          }
          /* Reduce active-state lift on mobile to avoid layout jitter */
          .step-card.is-visible.is-active {
            transform: translateY(0) scale(1);
          }
        }
        
        @media (min-width: 1025px) {
          .progress-container {
            display: block;
          }
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 lg:mb-20">
          <h2 className="text-sm font-semibold text-amber-400 tracking-wider uppercase mb-4">
            Simple Process
          </h2>
          <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white">
            How It Works
          </h3>
        </div>

        {/* Steps Container with Progress Line */}
        <div className="relative">
          {/* Progress Line Container - Left side */}
          <div className="progress-container absolute left-0 top-0 bottom-0 w-8">
            <div className="progress-rail" />
            <div 
              className="progress-fill" 
              style={{ height: `${progressHeight}%` }}
            />
            {/* Step dots */}
            {steps.map((_, index) => {
              const dotPosition = ((index + 0.5) / steps.length) * 100;
              const isPassed = progressHeight >= dotPosition;
              const isActiveDot = activeIndex === index;
              return (
                <div
                  key={index}
                  className={`step-dot ${isPassed ? 'is-passed' : ''} ${isActiveDot ? 'is-active' : ''}`}
                  style={{ top: `${dotPosition}%`, transform: `translateY(-50%)` }}
                />
              );
            })}
          </div>

          {/* Steps */}
          <div 
            ref={stepsContainerRef}
            className="steps-wrapper space-y-8 lg:pl-14"
          >
            {steps.map((step, index) => (
              <div
                key={step.id || index}
                ref={(el) => (stepRefs.current[index] = el)}
                data-step-index={index}
                className={`step-card ${visibleSteps.has(index) ? 'is-visible' : ''} ${activeIndex === index ? 'is-active' : ''}`}
                data-testid={`step-card-${index}`}
              >
                <div className="step-content-wrapper">
                  <div className={`flex flex-col lg:flex-row items-center gap-8 lg:gap-12 ${
                    index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}>
                    {/* Image */}
                    <div className="flex-1 w-full lg:w-auto">
                      <div className="relative group">
                        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 to-slate-700/20 rounded-2xl blur-2xl group-hover:blur-3xl transition-all duration-500 opacity-50" />
                        <div className="relative overflow-hidden rounded-2xl border border-slate-700/50">
                          <img
                            src={step.image}
                            alt={step.title}
                            className="w-full h-56 sm:h-64 object-cover transform group-hover:scale-105 transition-transform duration-700"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent" />
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 w-full lg:w-auto">
                      <div className="flex items-start gap-4 sm:gap-6">
                        {/* Step Number */}
                        <div className="flex-shrink-0">
                          <span className="step-number text-5xl sm:text-6xl font-bold">
                            {step.step}
                          </span>
                        </div>

                        {/* Text Content */}
                        <div className="pt-2 sm:pt-4">
                          <h4 className="text-xl sm:text-2xl font-bold text-white mb-3">
                            {step.title}
                          </h4>
                          <p className="text-slate-400 text-base sm:text-lg leading-relaxed text-justify">
                            {step.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
